import { Suspense } from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { usePortalAuth } from "@/lib/portal-auth";
import { LogOut } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { PageSpinner } from "@/components/ui/spinner";
import { LOGO_ICON_SRC } from "@/lib/brand";

const tabs = [
    { to: "/portal", label: "Dashboard", end: true },
    { to: "/portal/jobs", label: "Services" },
    { to: "/portal/invoices", label: "Invoices" },
    { to: "/portal/estimates", label: "Estimates" },
    // "Devices", not "Assets" — this is the CUSTOMER's navigation, and
    // "assets" is an accounting word for the thing on their wall.
    { to: "/portal/assets", label: "Devices" },
];

export function PortalShell() {
    const { user, loading, logout } = usePortalAuth();
    const navigate = useNavigate();

    if (loading) return <PageSpinner surface="card" />;
    if (!user) {
        navigate("/portal/login", { replace: true });
        return null;
    }

    return (
        <div className="min-h-screen bg-background">
            <header className="sticky top-0 z-30 border-b bg-card">
                <div className="mx-auto flex h-14 max-w-5xl items-center justify-between px-4">
                    {/* min-w-0 + truncate on BOTH halves: without it the
                        header alone pushed a 390px viewport 5px sideways. */}
                    <div className="flex min-w-0 items-center gap-2">
                        <img src={LOGO_ICON_SRC} alt="" className="h-6 w-6 shrink-0 rounded" />
                        <span className="truncate text-lg font-semibold">Customer Portal</span>
                    </div>
                    <div className="flex shrink-0 items-center gap-3">
                        <span className="max-w-[34vw] truncate text-sm text-foreground/80 sm:max-w-none">
                            {user.customerName}
                        </span>
                        <Button variant="ghost" size="sm" onClick={logout}>
                            <LogOut className="h-4 w-4 mr-1" />
                            Sign out
                        </Button>
                    </div>
                </div>
                {/*
                 * The five tabs are wider than a phone (they overflow a 390px
                 * viewport by ~110px), which scrolled the WHOLE page sideways
                 * — every screen, not just this strip. Scroll the strip
                 * itself instead, and let it shrink to fit when it can.
                 * Found shooting the #1430 proofs; predates that change.
                 */}
                <nav className="mx-auto max-w-5xl overflow-x-auto px-4">
                    <div className="-mb-px flex gap-1">
                        {[
                            ...tabs,
                            ...(user.showBudget ? [{ to: "/portal/budget", label: "Budget", end: false }] : []),
                        ].map((tab) => (
                            <NavLink
                                key={tab.to}
                                to={tab.to}
                                end={tab.end}
                                className={({ isActive }) =>
                                    cn(
                                        "shrink-0 whitespace-nowrap border-b-2 px-3 py-2.5 text-sm font-medium transition-colors sm:px-4",
                                        isActive
                                            ? "border-primary text-primary"
                                            : "border-transparent text-foreground/80 hover:text-foreground hover:border-border",
                                    )
                                }
                            >
                                {tab.label}
                            </NavLink>
                        ))}
                    </div>
                </nav>
            </header>
            <main className="mx-auto max-w-5xl px-4 py-6">
                <Suspense fallback={<PageSpinner surface="card" />}>
                    <Outlet />
                </Suspense>
            </main>
        </div>
    );
}
